import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import ContactModal from '../components/ContactModal';
import { Sparkles, MapPin, Calendar, Users, ArrowRight, CheckCircle, Monitor, Database, Handshake, Building, Zap, Target, Lightbulb } from 'lucide-react';

const VibeCoding = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');

    const openContactModal = (message) => {
        setModalMessage(message);
        setModalOpen(true);
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 py-12 pt-24 md:pt-12">
                {/* Hero Section */}
                <div className="text-center mb-16 relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-primary/20 rounded-full blur-3xl -z-10"></div>
                    <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-3 py-1 mb-6">
                        <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-primary text-xs font-mono font-bold tracking-wider uppercase">Programa Gratuito · Inicia Enero 2026</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6 leading-tight">
                        Vibe Coding <span className="text-primary">Iztacalco</span>
                    </h1>
                    <p className="text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                        Reactivación microeconómica mediante capacitación en desarrollo de software asistido por IA. Jóvenes locales creando herramientas digitales para el comercio de barrio.
                    </p>
                </div>

                {/* Call for Venue Alert */}
                <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-2xl p-6 mb-16 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                    <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                        <MapPin className="text-amber-400" size={32} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-2xl font-display font-bold text-white mb-2">¡Buscamos Sede!</h3>
                        <p className="text-zinc-300 mb-4">
                            Buscamos un espacio en Iztacalco (PILARES, Centro Cultural, Universidad, etc.) para alojar este piloto transformador. Necesitamos mesas, sillas y conexión a internet para 10 alumnos.
                        </p>
                        <button
                            onClick={() => openContactModal('Hola, me interesa ofrecer una sede para el programa Vibe Coding Iztacalco.')}
                            className="inline-flex items-center gap-2 bg-amber-500 text-black font-bold px-6 py-2 rounded-full hover:bg-amber-400 transition-colors"
                        >
                            Ofrecer Espacio <ArrowRight size={16} />
                        </button>
                    </div>
                </div>

                {/* The Problem & Vision */}
                <section className="mb-20">
                    <h2 className="text-3xl font-display font-bold text-white mb-8">El Contexto</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                                    <Target className="text-red-400" size={20} />
                                    El Problema
                                </h3>
                                <p className="text-zinc-400 text-sm leading-relaxed">
                                    Más del <strong className="text-white">90% de las unidades económicas</strong> en Iztacalco son microempresas familiares que operan con gestión empírica: inventarios visuales, comandas de memoria, contabilidad en cuadernos. Las soluciones comerciales (SAP, SaaS) son prohibitivas y el software empresarial aliena al usuario no técnico.
                                </p>
                            </div>
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                                    <Lightbulb className="text-primary" size={20} />
                                    La Oportunidad
                                </h3>
                                <p className="text-zinc-400 text-sm leading-relaxed">
                                    La irrupción de la <strong className="text-white">IA Generativa</strong> ha colapsado la barrera técnica. Ya no se necesita memorizar sintaxis; se necesita intención, lógica y creatividad. El "Vibe Coding" permite que jóvenes sin formación en ingeniería construyan software de producción en semanas.
                                </p>
                            </div>
                            <p className="text-lg text-white font-medium border-l-4 border-primary pl-4">
                                No enseñamos a usar software. Enseñamos a <span className="text-primary">construir</span> el software.
                            </p>
                        </div>
                        <div className="bg-surface border border-zinc-800 rounded-xl p-8 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-32 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                            <h3 className="text-xl font-bold text-white mb-6">El Modelo "Leapfrogging"</h3>
                            <p className="text-zinc-500 text-sm mb-6">Saltamos etapas de desarrollo digital tradicional.</p>
                            <ul className="space-y-5">
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                                        <Zap className="text-emerald-400" size={20} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-zinc-200">De Consumidor a Productor</p>
                                        <p className="text-sm text-zinc-500">Jóvenes de la colonia creando las apps que usan sus vecinos comerciantes.</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                        <Target className="text-blue-400" size={20} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-zinc-200">Software Hiper-Específico</p>
                                        <p className="text-sm text-zinc-500">No genérico: una app de inventario para la cremería Don Beto, no un SaaS global.</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                                        <CheckCircle className="text-purple-400" size={20} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-zinc-200">Soberanía Tecnológica</p>
                                        <p className="text-sm text-zinc-500">Cero licencias costosas. Firebase tiene tier gratuito generoso. El código es propiedad del barrio.</p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* The Tech Stack */}
                <section className="mb-20">
                    <h2 className="text-3xl font-display font-bold text-white mb-8">El Stack Agéntico</h2>
                    <p className="text-zinc-400 mb-8 max-w-2xl">
                        Herramientas de vanguardia que maximizan productividad y minimizan configuración.
                    </p>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-primary/50 transition-colors group">
                            <Monitor className="text-blue-400 mb-4 group-hover:scale-110 transition-transform" size={32} />
                            <h3 className="text-xl font-bold text-white mb-2">Google Antigravity</h3>
                            <p className="text-sm text-zinc-400">
                                IDE agéntico. El alumno dirige con lenguaje natural, los agentes escriben el código.
                            </p>
                        </div>
                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-purple-400/50 transition-colors group">
                            <Sparkles className="text-purple-400 mb-4 group-hover:scale-110 transition-transform" size={32} />
                            <h3 className="text-xl font-bold text-white mb-2">Gemini 3</h3>
                            <p className="text-sm text-zinc-400">
                                IA Multimodal. Capaz de "ver" un menú en papel y convertirlo en base de datos estructurada.
                            </p>
                        </div>
                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-orange-400/50 transition-colors group">
                            <Database className="text-orange-400 mb-4 group-hover:scale-110 transition-transform" size={32} />
                            <h3 className="text-xl font-bold text-white mb-2">Firebase</h3>
                            <p className="text-sm text-zinc-400">
                                Backend serverless. Base de datos en tiempo real, auth, hosting. Todo en tier gratuito.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Bootcamp Timeline - Improved */}
                <section className="mb-20 bg-surface rounded-2xl border border-zinc-800 p-8 md:p-12">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                        <div>
                            <h2 className="text-3xl font-display font-bold text-white">Bootcamp Vibe Coding</h2>
                            <p className="text-zinc-400">Inicia: <span className="text-primary font-mono">Tercera semana de Enero 2026</span></p>
                        </div>
                        <div className="flex gap-4 flex-wrap">
                            <div className="flex items-center gap-2 text-zinc-300 bg-zinc-800 px-3 py-1 rounded-full text-sm">
                                <Calendar size={14} /> 8 Semanas
                            </div>
                            <div className="flex items-center gap-2 text-zinc-300 bg-zinc-800 px-3 py-1 rounded-full text-sm">
                                <Users size={14} /> 10 Alumnos
                            </div>
                        </div>
                    </div>

                    {/* Timeline Cards */}
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-background rounded-xl p-6 border border-primary/30 relative">
                            <div className="absolute -top-3 left-6 bg-primary text-black text-xs font-bold px-3 py-1 rounded-full">
                                FASE 1
                            </div>
                            <h4 className="text-lg font-bold text-white mt-2 mb-1">Fundamentación</h4>
                            <p className="text-primary font-mono text-xs mb-3">Semanas 1-2</p>
                            <ul className="text-sm text-zinc-400 space-y-2">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-primary shrink-0 mt-0.5" size={14} />
                                    <span>Cambio de mentalidad: de consumidor a creador</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-primary shrink-0 mt-0.5" size={14} />
                                    <span>Ingeniería de Prompts + Product Management</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-primary shrink-0 mt-0.5" size={14} />
                                    <span>Entrevistas con comerciantes locales</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-background rounded-xl p-6 border border-zinc-700 relative">
                            <div className="absolute -top-3 left-6 bg-zinc-700 text-white text-xs font-bold px-3 py-1 rounded-full">
                                FASE 2
                            </div>
                            <h4 className="text-lg font-bold text-white mt-2 mb-1">Construcción</h4>
                            <p className="text-zinc-500 font-mono text-xs mb-3">Semanas 3-5</p>
                            <ul className="text-sm text-zinc-400 space-y-2">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-zinc-600 shrink-0 mt-0.5" size={14} />
                                    <span>Frontend con React + Tailwind CSS</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-zinc-600 shrink-0 mt-0.5" size={14} />
                                    <span>Backend con Firestore (NoSQL)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-zinc-600 shrink-0 mt-0.5" size={14} />
                                    <span>Autenticación y despliegue</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-background rounded-xl p-6 border border-zinc-700 relative">
                            <div className="absolute -top-3 left-6 bg-zinc-700 text-white text-xs font-bold px-3 py-1 rounded-full">
                                FASE 3
                            </div>
                            <h4 className="text-lg font-bold text-white mt-2 mb-1">Ejecución</h4>
                            <p className="text-zinc-500 font-mono text-xs mb-3">Semanas 6-8</p>
                            <ul className="text-sm text-zinc-400 space-y-2">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-zinc-600 shrink-0 mt-0.5" size={14} />
                                    <span>Hackathon: apps para negocios reales</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-zinc-600 shrink-0 mt-0.5" size={14} />
                                    <span>Mentoría de implementación</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="text-zinc-600 shrink-0 mt-0.5" size={14} />
                                    <span>Demo Day con comerciantes</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Sponsors Section */}
                <section className="mb-20">
                    <h2 className="text-3xl font-display font-bold text-white mb-4 text-center">Patrocinadores</h2>
                    <p className="text-zinc-400 text-center mb-8 max-w-xl mx-auto">
                        Este programa es posible gracias a alianzas público-privadas. Buscamos más patrocinadores y negocios locales interesados en implementar las soluciones.
                    </p>
                    <div className="flex flex-wrap justify-center items-center gap-8">
                        <a href="https://quantified.world" target="_blank" rel="noopener noreferrer" className="bg-surface border border-zinc-800 rounded-xl p-6 flex items-center gap-4 hover:border-primary/50 transition-colors group">
                            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                                <span className="text-2xl font-display font-bold text-primary">Q</span>
                            </div>
                            <div>
                                <p className="font-bold text-white group-hover:text-primary transition-colors">quantified.world</p>
                                <p className="text-xs text-zinc-500">Sponsor Principal</p>
                            </div>
                        </a>
                        <button
                            onClick={() => openContactModal('Hola, me interesa patrocinar el programa Vibe Coding Iztacalco.')}
                            className="bg-zinc-900/50 border border-dashed border-zinc-700 rounded-xl p-6 flex items-center gap-4 hover:border-primary/50 transition-colors cursor-pointer group"
                        >
                            <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center">
                                <Handshake className="text-zinc-500 group-hover:text-primary transition-colors" size={24} />
                            </div>
                            <div className="text-left">
                                <p className="font-bold text-zinc-400 group-hover:text-white transition-colors">¿Tu empresa aquí?</p>
                                <p className="text-xs text-zinc-600">Conviértete en patrocinador</p>
                            </div>
                        </button>
                    </div>
                </section>

                {/* Business Implementation CTA */}
                <section className="mb-20 bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border border-emerald-500/30 rounded-2xl p-8 text-center">
                    <Building className="mx-auto text-emerald-400 mb-4" size={48} />
                    <h3 className="text-2xl font-display font-bold text-white mb-3">¿Tienes un negocio en Iztacalco?</h3>
                    <p className="text-zinc-300 mb-6 max-w-lg mx-auto">
                        Los alumnos desarrollarán soluciones para comercios reales. Si tienes una fonda, taller, abarrote o negocio similar, podemos crear una herramienta digital a tu medida—sin costo.
                    </p>
                    <button
                        onClick={() => openContactModal('Hola, tengo un negocio en Iztacalco y me interesa que desarrollen una solución digital para mí.')}
                        className="inline-flex items-center gap-2 bg-emerald-500 text-black font-bold px-6 py-3 rounded-full hover:bg-emerald-400 transition-colors"
                    >
                        Postular mi Negocio <ArrowRight size={16} />
                    </button>
                </section>

                {/* Footer CTA */}
                <div className="text-center">
                    <h2 className="text-3xl font-display font-bold text-white mb-6">¿Te interesa?</h2>
                    <div className="flex flex-col md:flex-row justify-center gap-4">
                        <button
                            onClick={() => openContactModal('Hola, soy joven de Iztacalco y me interesa inscribirme al bootcamp Vibe Coding.')}
                            className="bg-primary hover:bg-primary/90 text-black font-bold py-3 px-8 rounded-full transition-colors"
                        >
                            Soy Estudiante (Lista de Espera)
                        </button>
                        <button
                            onClick={() => openContactModal('Hola, me interesa ser mentor o patrocinador del programa Vibe Coding.')}
                            className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 px-8 rounded-full transition-colors border border-zinc-700"
                        >
                            Quiero ser Mentor / Patrocinador
                        </button>
                    </div>
                </div>
            </div>

            {/* Contact Modal */}
            <ContactModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                prefillMessage={modalMessage}
            />
        </Layout>
    );
};

export default VibeCoding;
